import {
  ButtonBase,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import PageContainer from "../../../components/global/PageContainer"
import Title from "../../../components/global/Title"
import Button from "../../../components/global/Button"
import FadeFromTop from "../../../components/animations/FadeFromTop"
import { useContext, useEffect, useState } from "react"
import { colors } from "../../../services/config/colors"
import Card from "../../../components/global/Card"
import InfoCard from "../../../components/global/InfoCard"
import analyticsIcon from "../../../assets/icons/analytics.svg"
import Text from "../../../components/global/Text"
import infoPrimaryIcon from "../../../assets/icons/info-primary.svg"
import Tabs from "../../../components/global/Tabs"
import peopleLightIcon from "../../../assets/icons/people-light.svg"
import calendarLightIcon from "../../../assets/icons/calendar-light.svg"
import trophyIcon from "../../../assets/icons/trophy.svg"
import ChallengeProgressBar from "../../../components/challenge/ChallengeProgressBar"
import Select from "../../../components/global/Select"
import BarChart from "../../../components/global/BarChart"
import ChallengeLeaderboardTop3 from "../../../components/challenge/ChallengeLeaderboardTop3"
import ChallengeLeaderboardListItem from "../../../components/challenge/ChallengeLeaderboardListItem"
import { AnimatePresence, motion } from "framer-motion"
import playWhiteIcon from "../../../assets/icons/play-white.svg"
import actionWhiteIcon from "../../../assets/icons/action-white.svg"
import co2WhiteIcon from "../../../assets/icons/co2-white.svg"
import waterWhiteIcon from "../../../assets/icons/water-white.svg"
import energyWhiteIcon from "../../../assets/icons/energy-white.svg"
import dishwasherWhiteIcon from "../../../assets/icons/dishwasher-white.svg"
import bathtubeWhiteIcon from "../../../assets/icons/bathtube-white.svg"
import batteryWhiteIcon from "../../../assets/icons/battery-white.svg"
import TeamSavingsCard from "../../../components/savings/TeamSavingsCard"
import { useNavigate } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import { MainContext } from "../../../controllers/main"
import InfoCardSelectable from "../../../components/global/InfoCardSelectable"
import months from "../../../services/config/months"
import OverviewLineChart from "../../../components/overview/OverviewLineChart"
import OverviewIntervalSelect from "../../../components/overview/OverviewIntervalSelect"
import OverviewCardDataItem from "../../../models/overviewCardDataItem"
import {
  capitalizeFirstCharacter,
  convertNumberToHoursMinutes,
  formatNumber,
  roundWithDecimalPlaces,
} from "../../../services/utils/utils"
import ChallengeLeaderboardListItemSkeleton from "../../../components/challenge/ChallengeLeaderboardListItemSkeleton"
import AlertInfo from "../../../components/global/AlertInfo"
import { VisibilityOffRounded, VisibilityRounded } from "@mui/icons-material"
import { AuthContext } from "../../../controllers/auth"
import ChallengeOverviewDataItem from "../../../models/challengeOverviewDataItem"
import ChallengeOverviewLineChart from "../../../components/overview/ChallengeOverviewLineChart"
import ChallengeOverviewIntervalSelect from "../../../components/overview/ChallengeOverviewIntervalSelect"
import { OverviewContext } from "../../../controllers/overview"
import ChallengeGroupsLeaderboardTop3 from "../../../components/challenge/ChallengeGroupsLeaderboardTop3"
import ChallengeGroupsLeaderboardListItem from "../../../components/challenge/ChallengeGroupsLeaderboardListItem"
import ChallengeGroupsLeaderboardListItemSkeleton from "../../../components/challenge/ChallengeGroupsLeaderboardListItemSkeleton"

const Overview = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const matchesXL = useMediaQuery(theme.breakpoints.up("xl"))
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"))
  const { team } = useContext(AuthContext)
  const { categories } = useContext(MainContext)
  const {
    overviewDataLoading,
    getOverviewData,
    dashboardCardData,
    challenge,
    leaderboard,
    groupsLeaderboard,
    byCategoriesCardData,
    teamSavingsCardData,
    totalNews,
    totalSurveys,
  } = useContext(OverviewContext)

  // get data
  useEffect(() => {
    getOverviewData()
  }, [team])

  // challenge in progress selected tab
  const challengeTabs = [t("overview"), t("leaderboard"), t("groups")]
  const [challengeSelectedTab, setChallengeSelectedTab] = useState<number>(0)

  // challenge overview logic
  const challengeOverviewOptions = [
    t("weekly"),
    t("monthly"),
    t("annual"),
    t("total"),
  ]
  const [challengeOverviewSelectedOption, setChallengeOverviewSelectedOption] =
    useState<number>(1)
  const [challengeOverviewInterval, setChallengeOverviewInterval] = useState<{
    start: Date
    end: Date
  }>({
    start: new Date(new Date().getFullYear(), 0, 1, new Date().getHours()),
    end: new Date(new Date().getFullYear(), 11, 31, new Date().getHours()),
  })
  const [challengeOverviewSelectedItem, setChallengeOverviewSelectedItem] =
    useState<number>(0)
  const [dataForChallengeOverviewChart, setDataForChallengeOverviewChart] =
    useState<ChallengeOverviewDataItem[]>([])

  // content overview logic
  const contentOptions = [t("weekly"), t("monthly"), t("annual"), t("total")]
  const [contentSelectedOption, setContentSelectedOption] = useState<number>(1)
  const [contentInterval, setContentInterval] = useState<{
    start: Date
    end: Date
  }>({
    start: new Date(new Date().getFullYear(), 0, 1, new Date().getHours()),
    end: new Date(new Date().getFullYear(), 11, 31, new Date().getHours()),
  })
  const [contentSelectedItem, setContentSelectedItem] = useState<number>(0)
  const [dataForChart, setDataForChart] = useState<OverviewCardDataItem[]>([])
  const [dataVisibility, setDataVisibility] = useState<boolean[]>([true, true])

  // by categories tabs
  const categoriesTabs = [t("episodes"), t("actions")]
  const [categoriesSelectedTab, setCategoriesSelectedTab] = useState<number>(0)

  // info alerts
  const [challengeAlertOpen, setChallengeAlertOpen] = useState<boolean>(false)
  const [overviewAlertOpen, setOverviewAlertOpen] = useState<boolean>(false)
  const [savingsAlertOpen, setSavingsAlertOpen] = useState<boolean>(false)

  return (
    <PageContainer
      title={t("menu_overview")}
      breadcrumbs={[{ title: t("menu_overview") }]}
      infoText={t("overview_info")}
    >
      {/* dashboard */}
      <FadeFromTop>
        <Card>
          <Stack direction={matchesMD ? "column" : "row"} gap={3}>
            <InfoCard loading={overviewDataLoading}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                style={{ width: "100%", height: 54 }}
              >
                <div
                  className="center"
                  style={{
                    width: 50,
                    height: 50,
                    backgroundColor: colors.primary,
                    borderRadius: 7,
                  }}
                >
                  <img
                    src={analyticsIcon}
                    style={{ width: 25, height: 25 }}
                    alt=""
                  />
                </div>
                <Stack>
                  <Text
                    fontWeight={500}
                    lineHeight="24px"
                    color={colors.textSecondary}
                  >
                    {t("total_users")}
                  </Text>
                  <Title
                    fontSize={22}
                    lineHeight="30px"
                    loading={overviewDataLoading}
                  >
                    {dashboardCardData
                      ? formatNumber(dashboardCardData.totalUsers)
                      : t("no_data")}
                  </Title>
                </Stack>
              </Stack>
              <Text
                fontSize={14}
                fontWeight={500}
                color={colors.success}
                style={{ marginTop: 16 }}
                loading={overviewDataLoading}
              >
                {dashboardCardData?.activeUsersPercentage
                  ? t("active_users_percentage", {
                      n: formatNumber(dashboardCardData?.activeUsersPercentage),
                    })
                  : t("no_data")}
              </Text>
            </InfoCard>
            <InfoCard
              clickable={team?.isPrimary}
              onClick={() => {
                navigate("/overview/education-hours")
              }}
              loading={overviewDataLoading}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                style={{ width: "100%", height: 54 }}
              >
                <div
                  className="center"
                  style={{
                    width: 50,
                    height: 50,
                    backgroundColor: colors.primary,
                    borderRadius: 7,
                  }}
                >
                  <img
                    src={analyticsIcon}
                    style={{ width: 25, height: 25 }}
                    alt=""
                  />
                </div>
                <Stack>
                  <Text
                    fontWeight={500}
                    lineHeight="24px"
                    color={colors.textSecondary}
                  >
                    {t("total_education_hours")}
                  </Text>
                  <Title
                    fontSize={22}
                    lineHeight="30px"
                    loading={overviewDataLoading}
                  >
                    {dashboardCardData?.totalEducationHours
                      ? `${formatNumber(
                          dashboardCardData?.totalEducationHours
                        )} ${t("hours")}`
                      : t("no_data")}
                  </Title>
                </Stack>
              </Stack>
              <Text
                fontSize={14}
                fontWeight={500}
                color={colors.success}
                style={{ marginTop: 16 }}
                loading={overviewDataLoading}
              >
                {dashboardCardData?.avgEducationHoursPerUser
                  ? t("per_person_average", {
                      amount: convertNumberToHoursMinutes(
                        dashboardCardData?.avgEducationHoursPerUser!
                      ),
                    })
                  : t("no_data")}
              </Text>
            </InfoCard>
          </Stack>
          <Stack direction={matchesMD ? "column" : "row"} gap={3}>
            <InfoCard loading={overviewDataLoading}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                style={{ width: "100%", height: 54 }}
              >
                <div
                  className="center"
                  style={{
                    width: 50,
                    height: 50,
                    backgroundColor: colors.primary,
                    borderRadius: 7,
                  }}
                >
                  <img
                    src={analyticsIcon}
                    style={{ width: 25, height: 25 }}
                    alt=""
                  />
                </div>
                <Stack>
                  <Text
                    fontWeight={500}
                    lineHeight="24px"
                    color={colors.textSecondary}
                  >
                    {t("total_news")}
                  </Text>
                  <Title
                    fontSize={22}
                    lineHeight="30px"
                    loading={overviewDataLoading}
                  >
                    {totalNews !== null
                      ? formatNumber(totalNews)
                      : t("no_data")}
                  </Title>
                </Stack>
              </Stack>
            </InfoCard>
            <InfoCard loading={overviewDataLoading}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                style={{ width: "100%", height: 54 }}
              >
                <div
                  className="center"
                  style={{
                    width: 50,
                    height: 50,
                    backgroundColor: colors.primary,
                    borderRadius: 7,
                  }}
                >
                  <img
                    src={analyticsIcon}
                    style={{ width: 25, height: 25 }}
                    alt=""
                  />
                </div>
                <Stack>
                  <Text
                    fontWeight={500}
                    lineHeight="24px"
                    color={colors.textSecondary}
                  >
                    {t("total_surveys")}
                  </Text>
                  <Title
                    fontSize={22}
                    lineHeight="30px"
                    loading={overviewDataLoading}
                  >
                    {totalSurveys !== null
                      ? formatNumber(totalSurveys)
                      : t("no_data")}
                  </Title>
                </Stack>
              </Stack>
            </InfoCard>
          </Stack>
        </Card>
      </FadeFromTop>
      {/* challenge in progress */}
      <FadeFromTop
        delay={0.05}
        style={{
          marginTop: 24,
        }}
      >
        <Stack direction={{ lg: "column", xl: "row" }} gap={3}>
          <Card width={matchesXL ? "calc(60% - 12px)" : "100%"}>
            <ButtonBase
              title="Info"
              style={{
                width: 28,
                height: 28,
                position: "absolute",
                right: 24,
                borderRadius: "100%",
              }}
              onClick={() => {
                setChallengeAlertOpen(true)
              }}
            >
              <img
                src={infoPrimaryIcon}
                style={{ width: "100%", height: "100%" }}
              />
            </ButtonBase>
            <Title style={{ maxWidth: "90%" }}>{t("challenge")}</Title>
            {!overviewDataLoading && !challenge ? (
              <>
                <Text style={{ flex: 1 }}>{t("no_challenge_for_team")}</Text>
                <Button
                  primary
                  size="normal"
                  onClick={() => {
                    navigate("/community-missions")
                  }}
                >
                  {t("view_past_challenges")}
                </Button>
              </>
            ) : null}
            {challenge || overviewDataLoading ? (
              <Tabs
                tabs={challengeTabs}
                selectedTab={challengeSelectedTab}
                setSelectedTab={setChallengeSelectedTab}
              />
            ) : null}
            {challenge || overviewDataLoading ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 330,
                  position: "relative",
                }}
              >
                <AnimatePresence>
                  <motion.div
                    key={challengeSelectedTab}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{
                      scale: { duration: 0.4, type: "spring" },
                      opacity: { duration: 0.1 },
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "absolute",
                    }}
                  >
                    {challengeSelectedTab === 0 ? (
                      <Stack gap={2}>
                        <Stack direction="row" style={{ gap: 20 }}>
                          <Stack style={{ maxWidth: "60%" }}>
                            <Title
                              fontSize={22}
                              lineHeight="28px"
                              loading={overviewDataLoading}
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {challenge?.document.title}
                            </Title>
                            <Stack
                              direction="row"
                              alignItems="center"
                              style={{ height: 24, marginTop: 12, gap: 7 }}
                            >
                              <img
                                src={peopleLightIcon}
                                style={{ width: 16 }}
                              />
                              <Text
                                color={colors.textSecondary}
                                loading={overviewDataLoading}
                              >
                                {formatNumber(challenge?.users)}{" "}
                                {t("participants")} /{" "}
                                {formatNumber(challenge?.activeUsers)}{" "}
                                {t("active")}
                              </Text>
                            </Stack>
                            <Stack
                              direction="row"
                              alignItems="center"
                              style={{ height: 24, marginTop: 6, gap: 7 }}
                            >
                              <img
                                src={calendarLightIcon}
                                style={{ width: 16 }}
                              />
                              <Text
                                color={colors.textSecondary}
                                loading={overviewDataLoading}
                              >
                                {challenge
                                  ? `${new Date(
                                      challenge.startsAt
                                    ).getDate()} ${t(
                                      months[
                                        new Date(challenge.startsAt).getMonth()
                                      ]
                                    ).slice(0, 3)} ${new Date(
                                      challenge.startsAt
                                    ).getFullYear()} - ${new Date(
                                      challenge.endsAt
                                    ).getDate()} ${t(
                                      months[
                                        new Date(challenge.endsAt).getMonth()
                                      ]
                                    ).slice(0, 3)} ${new Date(
                                      challenge.endsAt
                                    ).getFullYear()}`
                                  : ""}
                              </Text>
                            </Stack>
                          </Stack>
                        </Stack>
                        <InfoCard loading={overviewDataLoading}>
                          <Stack gap={0.5}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              style={{ height: 25, gap: 9 }}
                            >
                              <img src={trophyIcon} style={{ width: 18 }} />
                              <Title
                                fontSize={18}
                                lineHeight="20px"
                                loading={overviewDataLoading}
                              >
                                {t("goal")}:{" "}
                                {formatNumber(challenge?.targetAmount)}{" "}
                                {challenge?.metric.name}
                              </Title>
                            </Stack>
                            <Text
                              fontSize={14}
                              fontWeight={600}
                              loading={overviewDataLoading}
                            >
                              {`${t("total")}: ${formatNumber(
                                challenge?.currentAmount
                              )} ${challenge?.metric.name}`}
                            </Text>
                          </Stack>
                          <ChallengeProgressBar
                            percentage={
                              challenge
                                ? roundWithDecimalPlaces(
                                    (challenge.currentAmount /
                                      challenge.targetAmount) *
                                      100,
                                    1
                                  )
                                : 0
                            }
                            loading={overviewDataLoading}
                            style={{ marginTop: 10 }}
                          />
                        </InfoCard>
                        <Button
                          primary
                          disabled={overviewDataLoading}
                          size="normal"
                          style={{
                            position: "absolute",
                            bottom: 0,
                            width: "100%",
                          }}
                          onClick={() => {
                            navigate("/community-missions")
                          }}
                        >
                          {t("view_past_challenges")}
                        </Button>
                      </Stack>
                    ) : challengeSelectedTab === 1 ? (
                      <Stack gap={3}>
                        <ChallengeLeaderboardTop3
                          loading={overviewDataLoading}
                        />
                        <Stack
                          style={{
                            width: "100%",
                            marginTop: -12,
                          }}
                        >
                          {overviewDataLoading ? (
                            <ChallengeLeaderboardListItemSkeleton />
                          ) : leaderboard[3] ? (
                            <ChallengeLeaderboardListItem
                              place={4}
                              user={leaderboard[3].user}
                              points={leaderboard[3].userPoints}
                              loading={overviewDataLoading}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 66,
                              }}
                            />
                          )}
                          {leaderboard[4] ? (
                            <div
                              style={{
                                width: "100%",
                                height: 2,
                                backgroundColor: colors.backgroundLight,
                              }}
                            />
                          ) : null}
                          {overviewDataLoading ? (
                            <ChallengeLeaderboardListItemSkeleton />
                          ) : leaderboard[4] ? (
                            <ChallengeLeaderboardListItem
                              place={5}
                              user={leaderboard[4].user}
                              points={leaderboard[4].userPoints}
                              loading={overviewDataLoading}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 66,
                              }}
                            />
                          )}
                        </Stack>
                        <Button
                          primary
                          disabled={
                            overviewDataLoading || leaderboard.length <= 5
                          }
                          size="normal"
                          style={{
                            width: "100%",
                            position: "absolute",
                            bottom: 0,
                          }}
                          onClick={() => {
                            navigate("/overview/leaderboard")
                          }}
                        >
                          {t("view_all")}
                        </Button>
                      </Stack>
                    ) : (
                      <Stack gap={3}>
                        <ChallengeGroupsLeaderboardTop3
                          loading={overviewDataLoading}
                        />
                        <Stack
                          style={{
                            width: "100%",
                            marginTop: -12,
                          }}
                        >
                          {overviewDataLoading ? (
                            <ChallengeGroupsLeaderboardListItemSkeleton />
                          ) : groupsLeaderboard[3] ? (
                            <ChallengeGroupsLeaderboardListItem
                              place={4}
                              groupId={groupsLeaderboard[3].groupId}
                              points={groupsLeaderboard[3].groupPoints}
                              loading={overviewDataLoading}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 66,
                              }}
                            />
                          )}
                          {groupsLeaderboard[4] ? (
                            <div
                              style={{
                                width: "100%",
                                height: 2,
                                backgroundColor: colors.backgroundLight,
                              }}
                            />
                          ) : null}
                          {overviewDataLoading ? (
                            <ChallengeGroupsLeaderboardListItemSkeleton />
                          ) : groupsLeaderboard[4] ? (
                            <ChallengeGroupsLeaderboardListItem
                              place={5}
                              groupId={groupsLeaderboard[4].groupId}
                              points={groupsLeaderboard[4].groupPoints}
                              loading={overviewDataLoading}
                            />
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 66,
                              }}
                            />
                          )}
                        </Stack>
                        <Button
                          primary
                          disabled={
                            overviewDataLoading || groupsLeaderboard.length <= 5
                          }
                          size="normal"
                          style={{
                            width: "100%",
                            position: "absolute",
                            bottom: 0,
                          }}
                          onClick={() => {
                            navigate("/overview/groups-leaderboard")
                          }}
                        >
                          {t("view_all")}
                        </Button>
                      </Stack>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            ) : null}
          </Card>
          <Card gap={2} width={matchesXL ? "calc(40% - 12px)" : "100%"}>
            <ButtonBase
              title="Info"
              style={{
                width: 28,
                height: 28,
                position: "absolute",
                right: 24,
                borderRadius: "100%",
              }}
              onClick={() => {
                setSavingsAlertOpen(true)
              }}
            >
              <img
                src={infoPrimaryIcon}
                style={{ width: "100%", height: "100%" }}
              />
            </ButtonBase>
            <Title>{t("team_savings")}</Title>
            <TeamSavingsCard
              icon1={co2WhiteIcon}
              value1={teamSavingsCardData ? teamSavingsCardData.co2Saving : 0}
              label1={t("kg_co2_saved")}
              icon2={batteryWhiteIcon}
              value2={
                teamSavingsCardData ? teamSavingsCardData.phoneChargeSaving : 0
              }
              label2={t("mobile_battery_charges")}
              loading={overviewDataLoading}
              style={{ marginTop: 8 }}
            />
            <TeamSavingsCard
              icon1={waterWhiteIcon}
              value1={teamSavingsCardData ? teamSavingsCardData.waterSaving : 0}
              label1={t("l_water_saved")}
              icon2={bathtubeWhiteIcon}
              value2={
                teamSavingsCardData ? teamSavingsCardData.bathTubSaving : 0
              }
              label2={t("bathtubes_filled")}
              loading={overviewDataLoading}
            />
            <TeamSavingsCard
              icon1={energyWhiteIcon}
              value1={
                teamSavingsCardData ? teamSavingsCardData.energySaving : 0
              }
              label1={t("kwh_energy_saved")}
              icon2={dishwasherWhiteIcon}
              value2={
                teamSavingsCardData ? teamSavingsCardData.dishWasherSaving : 0
              }
              label2={t("dishwasher_cycles")}
              loading={overviewDataLoading}
            />
          </Card>
        </Stack>
      </FadeFromTop>
      {/* challenge overview */}
      {challenge || overviewDataLoading ? (
        <FadeFromTop
          delay={0.1}
          style={{
            marginTop: 24,
          }}
        >
          <Card style={{ overflowX: "scroll" }}>
            <Stack gap={3} style={{ minWidth: 880 }}>
              <Title>{t("challenge_overview")}</Title>
              <Stack
                direction="row"
                alignItems="center"
                style={{
                  width: "100%",
                  height: 69,
                  position: "relative",
                }}
              >
                <Stack style={{ gap: 12 }}>
                  {challengeOverviewSelectedOption !== 3 && (
                    <Title fontSize={16} lineHeight="19px">
                      {t("progression_view")}
                    </Title>
                  )}
                  <ChallengeOverviewIntervalSelect
                    selectedOption={challengeOverviewSelectedOption}
                    interval={challengeOverviewInterval}
                    setInterval={setChallengeOverviewInterval}
                    loading={overviewDataLoading}
                  />
                </Stack>
                <Stack style={{ position: "absolute", right: 0, gap: 6 }}>
                  <Text
                    fontSize={14}
                    lineHeight="14px"
                    fontWeight={500}
                    color={colors.primary}
                  >
                    {t("show")}
                  </Text>
                  <Select
                    options={challengeOverviewOptions}
                    selectedOption={challengeOverviewSelectedOption}
                    onChange={(newOption) =>
                      setChallengeOverviewSelectedOption(newOption)
                    }
                    popoverWidth={160}
                    style={{ width: 160 }}
                    loading={overviewDataLoading}
                  />
                </Stack>
              </Stack>
              <Stack direction="row" gap={3}>
                <Stack
                  justifyContent="flext-start"
                  style={{ width: "30%", height: 230, gap: 20 }}
                >
                  {/* actions completed */}
                  <InfoCardSelectable
                    size="small"
                    selected={challengeOverviewSelectedItem === 0}
                    onClick={() => {
                      setChallengeOverviewSelectedItem(0)
                    }}
                    loading={overviewDataLoading}
                  >
                    <Stack direction="row" gap={1}>
                      <div
                        className="center"
                        style={{
                          width: 40,
                          minWidth: 40,
                          height: 40,
                          borderRadius: 10,
                          backgroundColor: colors.primary,
                        }}
                      >
                        <img
                          src={actionWhiteIcon}
                          style={{ width: 24 }}
                          alt=""
                        />
                      </div>
                      <Stack
                        justifyContent="center"
                        gap={0.5}
                        style={{ width: "100%", position: "relative" }}
                      >
                        <Text
                          fontSize={12}
                          lineHeight="14px"
                          fontWeight={500}
                          color={colors.textSecondary}
                        >
                          {challenge
                            ? capitalizeFirstCharacter(challenge.metric.name)
                            : null}
                        </Text>
                        <Title
                          fontSize={16}
                          lineHeight="19px"
                          loading={overviewDataLoading}
                          skeletonWidth={50}
                        >
                          {formatNumber(
                            dataForChallengeOverviewChart.reduce(
                              (currentValue, item) =>
                                currentValue + item.activityAmount,
                              0
                            )
                          )}
                        </Title>
                      </Stack>
                    </Stack>
                  </InfoCardSelectable>
                </Stack>
                <Stack style={{ width: "70%" }}>
                  <div
                    style={{
                      width: "100%",
                      height: 230,
                      position: "relative",
                    }}
                  >
                    <ChallengeOverviewLineChart
                      selectedItem={challengeOverviewSelectedItem}
                      interval={challengeOverviewInterval}
                      dataForChart={dataForChallengeOverviewChart}
                      setDataForChart={setDataForChallengeOverviewChart}
                      selectedOption={challengeOverviewSelectedOption}
                      loading={overviewDataLoading}
                    />
                  </div>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </FadeFromTop>
      ) : null}
      {/* content overview */}
      <FadeFromTop
        delay={0.15}
        style={{
          marginTop: 24,
        }}
      >
        <Card style={{ overflowX: "scroll" }}>
          <Stack gap={3} style={{ minWidth: 880 }}>
            <ButtonBase
              title="Info"
              style={{
                width: 28,
                height: 28,
                position: "absolute",
                right: 24,
                borderRadius: "100%",
              }}
              onClick={() => {
                setOverviewAlertOpen(true)
              }}
            >
              <img
                src={infoPrimaryIcon}
                style={{ width: "100%", height: "100%" }}
              />
            </ButtonBase>
            <Title>{t("overview_overtime")}</Title>
            <Stack
              direction="row"
              alignItems="center"
              style={{
                width: "100%",
                height: 69,
                position: "relative",
              }}
            >
              <Stack style={{ gap: 12 }}>
                {contentSelectedOption !== 3 && (
                  <Title fontSize={16} lineHeight="19px">
                    {t("progression_view")}
                  </Title>
                )}
                <OverviewIntervalSelect
                  selectedOption={contentSelectedOption}
                  interval={contentInterval}
                  setInterval={setContentInterval}
                  loading={overviewDataLoading}
                />
              </Stack>
              <Stack style={{ position: "absolute", right: 0, gap: 6 }}>
                <Text
                  fontSize={14}
                  lineHeight="14px"
                  fontWeight={500}
                  color={colors.primary}
                >
                  {t("show")}
                </Text>
                <Select
                  options={contentOptions}
                  selectedOption={contentSelectedOption}
                  onChange={(newOption) => setContentSelectedOption(newOption)}
                  popoverWidth={160}
                  style={{ width: 160 }}
                  loading={overviewDataLoading}
                />
              </Stack>
            </Stack>
            <Stack direction="row" gap={3}>
              <Stack
                justifyContent="flext-start"
                style={{ width: "30%", height: 230, gap: 20 }}
              >
                {/* episodes completed */}
                <InfoCardSelectable
                  size="small"
                  selected={contentSelectedItem === 0}
                  onClick={() => {
                    setContentSelectedItem(0)
                  }}
                  loading={overviewDataLoading}
                >
                  <Stack direction="row" gap={1}>
                    <div
                      className="center"
                      style={{
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: colors.primary,
                      }}
                    >
                      <img src={playWhiteIcon} style={{ width: 15 }} alt="" />
                    </div>
                    <Stack
                      justifyContent="center"
                      gap={0.5}
                      style={{ width: "100%", position: "relative" }}
                    >
                      <Text
                        fontSize={12}
                        lineHeight="14px"
                        fontWeight={500}
                        color={colors.textSecondary}
                      >
                        {t("episodes_completed")}
                      </Text>
                      <Title
                        fontSize={16}
                        lineHeight="19px"
                        loading={overviewDataLoading}
                        skeletonWidth={50}
                      >
                        {formatNumber(
                          dataForChart
                            .filter((item) => item.activityType === "episode")
                            .reduce(
                              (currentValue, item) =>
                                currentValue + item.activityAmount,
                              0
                            )
                        )}
                      </Title>
                      <div
                        role="button"
                        className="center"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "100%",
                          position: "absolute",
                          right: 0,
                        }}
                        onClick={(e) => {
                          if (!overviewDataLoading) {
                            e.stopPropagation()

                            setDataVisibility((current) => [
                              !current[0],
                              current[1],
                            ])
                          }
                        }}
                      >
                        <VisibilityRounded
                          style={{
                            color: colors.primary,
                            fontSize: 18,
                            position: "absolute",
                            opacity: overviewDataLoading
                              ? 0.5
                              : dataVisibility[0]
                              ? 1
                              : 0,
                            transition: "250ms",
                          }}
                        />
                        <VisibilityOffRounded
                          style={{
                            color: colors.primary,
                            fontSize: 18,
                            position: "absolute",
                            opacity:
                              dataVisibility[0] || overviewDataLoading ? 0 : 1,
                            transition: "400ms",
                          }}
                        />
                      </div>
                    </Stack>
                  </Stack>
                </InfoCardSelectable>
                {/* actions completed */}
                <InfoCardSelectable
                  size="small"
                  selected={contentSelectedItem === 1}
                  onClick={() => {
                    setContentSelectedItem(1)
                  }}
                  loading={overviewDataLoading}
                >
                  <Stack direction="row" gap={1}>
                    <div
                      className="center"
                      style={{
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: colors.primary,
                      }}
                    >
                      <img src={actionWhiteIcon} style={{ width: 24 }} alt="" />
                    </div>
                    <Stack
                      justifyContent="center"
                      gap={0.5}
                      style={{ width: "100%", position: "relative" }}
                    >
                      <Text
                        fontSize={12}
                        lineHeight="14px"
                        fontWeight={500}
                        color={colors.textSecondary}
                      >
                        {t("actions_completed")}
                      </Text>
                      <Title
                        fontSize={16}
                        lineHeight="19px"
                        loading={overviewDataLoading}
                        skeletonWidth={50}
                      >
                        {formatNumber(
                          dataForChart
                            .filter((item) => item.activityType === "action")
                            .reduce(
                              (currentValue, item) =>
                                currentValue + item.activityAmount,
                              0
                            )
                        )}
                      </Title>
                      <div
                        role="button"
                        className="center"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "100%",
                          position: "absolute",
                          right: 0,
                        }}
                        onClick={(e) => {
                          if (!overviewDataLoading) {
                            e.stopPropagation()

                            setDataVisibility((current) => [
                              current[0],
                              !current[1],
                            ])
                          }
                        }}
                      >
                        <VisibilityRounded
                          style={{
                            color: colors.primary,
                            fontSize: 18,
                            position: "absolute",
                            opacity: overviewDataLoading
                              ? 0.5
                              : dataVisibility[1]
                              ? 1
                              : 0,
                            transition: "250ms",
                          }}
                        />
                        <VisibilityOffRounded
                          style={{
                            color: colors.primary,
                            fontSize: 18,
                            position: "absolute",
                            opacity:
                              dataVisibility[1] || overviewDataLoading ? 0 : 1,
                            transition: "400ms",
                          }}
                        />
                      </div>
                    </Stack>
                  </Stack>
                </InfoCardSelectable>
              </Stack>
              <Stack style={{ width: "70%" }}>
                <div
                  style={{
                    width: "100%",
                    height: 230,
                    position: "relative",
                  }}
                >
                  <OverviewLineChart
                    selectedItem={contentSelectedItem}
                    interval={contentInterval}
                    dataForChart={dataForChart}
                    setDataForChart={setDataForChart}
                    dataVisibility={dataVisibility}
                    selectedOption={contentSelectedOption}
                    loading={overviewDataLoading}
                  />
                </div>
              </Stack>
            </Stack>
            <div
              style={{
                width: "100%",
                height: 17,
                position: "relative",
              }}
            >
              <Stack
                direction="row"
                gap={2}
                alignItems="center"
                style={{ position: "absolute", right: 0 }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "100%",
                      backgroundColor: colors.categories.episodes,
                    }}
                  />
                  <Text
                    fontSize={14}
                    fontWeight={500}
                    color={colors.textSecondary}
                  >
                    {t("episodes")}
                  </Text>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "100%",
                      backgroundColor: colors.categories.actions,
                    }}
                  />
                  <Text
                    fontSize={14}
                    fontWeight={500}
                    color={colors.textSecondary}
                  >
                    {t("actions")}
                  </Text>
                </Stack>
              </Stack>
            </div>
          </Stack>
        </Card>
      </FadeFromTop>
      {/* by category and team savings */}
      <FadeFromTop
        delay={0.2}
        style={{
          marginTop: 24,
        }}
      >
        <Card gap={0} style={{ overflowX: "scroll" }}>
          <Stack style={{ minWidth: 540 }}>
            <Stack
              direction="row"
              alignItems="center"
              style={{ position: "relative" }}
            >
              <Title>{t("by_categories")}</Title>
              <Title
                style={{ position: "absolute", right: 0 }}
                loading={overviewDataLoading}
              >
                {t("total")}:{" "}
                <span style={{ color: colors.primary }}>
                  {categoriesSelectedTab === 0
                    ? formatNumber(
                        byCategoriesCardData
                          .filter(
                            (item) =>
                              item.activityType === "episode" &&
                              item.activityCategory
                          )
                          .reduce(
                            (accumulator, item) =>
                              accumulator + item.activityAmount,
                            0
                          )
                      )
                    : formatNumber(
                        byCategoriesCardData
                          .filter(
                            (item) =>
                              item.activityType === "action" &&
                              item.activityCategory
                          )
                          .reduce(
                            (accumulator, item) =>
                              accumulator + item.activityAmount,
                            0
                          )
                      )}
                </span>
              </Title>
            </Stack>
            <Tabs
              tabs={categoriesTabs}
              selectedTab={categoriesSelectedTab}
              setSelectedTab={setCategoriesSelectedTab}
              style={{ width: 362, maxWidth: "100%", marginTop: 20 }}
            />
            <Text
              fontSize={14}
              lineHeight="20px"
              color={colors.primary}
              style={{ marginTop: 10 }}
            >
              {categoriesSelectedTab === 0
                ? capitalizeFirstCharacter(t("views"))
                : capitalizeFirstCharacter(t("logs"))}
            </Text>
            <div
              style={{
                width: "100%",
                height: 246,
                position: "relative",
                marginTop: 10,
              }}
            >
              <BarChart
                xTicksFontSize={9}
                data={
                  categoriesSelectedTab === 0
                    ? byCategoriesCardData
                        .filter(
                          (item) =>
                            item.activityType === "episode" &&
                            item.activityCategory
                        )
                        .sort((a, b) => {
                          if (a.activityCategory < b.activityCategory) {
                            return -1
                          } else if (a.activityCategory > b.activityCategory) {
                            return 1
                          }
                          return 0
                        })
                        .map((item) => {
                          const currentCategory = categories.find(
                            (category) => category.id === item.activityCategory
                          )

                          if (!currentCategory) {
                            return null
                          }

                          return {
                            label: currentCategory.name,
                            value: item.activityAmount,
                            backgroundColor: currentCategory.backTagColor,
                            color: currentCategory.foreColor,
                          }
                        })
                    : byCategoriesCardData
                        .filter(
                          (item) =>
                            item.activityType === "action" &&
                            item.activityCategory
                        )
                        .sort((a, b) => {
                          if (a.activityCategory < b.activityCategory) {
                            return -1
                          } else if (a.activityCategory > b.activityCategory) {
                            return 1
                          }
                          return 0
                        })
                        .map((item) => {
                          const currentCategory = categories.find(
                            (category) => category.id === item.activityCategory
                          )

                          if (!currentCategory) {
                            return null
                          }

                          return {
                            label: currentCategory.name,
                            value: item.activityAmount,
                            backgroundColor: currentCategory.backTagColor,
                            color: currentCategory.foreColor,
                          }
                        })
                }
                loading={overviewDataLoading}
              />
            </div>
          </Stack>
        </Card>
      </FadeFromTop>
      {/* alerts */}
      <AlertInfo
        open={challengeAlertOpen}
        setOpen={setChallengeAlertOpen}
        title={t("challenge")}
        description={t("challenge_description")}
      />
      <AlertInfo
        open={overviewAlertOpen}
        setOpen={setOverviewAlertOpen}
        title={t("overview_overtime")}
        description={t("overview_overtime_description")}
      />
      <AlertInfo
        open={savingsAlertOpen}
        setOpen={setSavingsAlertOpen}
        title={t("team_savings")}
        description={t("team_savings_description")}
      />
    </PageContainer>
  )
}

export default Overview
